"use client"

import { useState, useEffect } from "react"
import { Button } from "../../../components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog"
import { Input } from "../../../components/ui/input"
import { Label } from "../../../components/ui/label"
import { Textarea } from "../../../components/ui/textarea"
import { Info } from "lucide-react"
import type { SubcategoryInfo } from "../../../lib/types"

export const CURRENCY_OPTIONS = [
  { code: "BRL", symbol: "R$", label: "Real (R$)" },
  { code: "USD", symbol: "US$", label: "Dólar (US$)" },
  { code: "EUR", symbol: "€", label: "Euro (€)" },
  { code: "GBP", symbol: "£", label: "Libra (£)" },
  { code: "ARS", symbol: "AR$", label: "Peso Argentino (AR$)" },
]

// Formata o valor numérico + moeda salvos na subcategoria pra exibição
// (ex: "80" + "USD" -> "US$ 80,00"). Se o texto digitado não for um número
// (alguém colou "R$ 80,00 à vista" no campo, por exemplo), mostra como foi
// digitado em vez de quebrar.
export function formatSubcategoryPrice(price?: string, currency?: string): string {
  if (!price) return ""
  const num = Number(String(price).replace(",", "."))
  if (Number.isNaN(num)) return price
  try {
    return new Intl.NumberFormat("pt-BR", { style: "currency", currency: currency || "BRL" }).format(num)
  } catch {
    const symbol = CURRENCY_OPTIONS.find((c) => c.code === currency)?.symbol || "R$"
    return `${symbol} ${price}`
  }
}

interface SubcategoryInfoModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  subcategoryName: string
  initialInfo?: SubcategoryInfo
  onSave: (info: SubcategoryInfo) => void
}

const emptyInfo: SubcategoryInfo = {
  price: "",
  currency: "BRL",
  fabric: "",
  sleeves: "",
  collar: "",
  differentials: "",
  benefits: "",
}

export function SubcategoryInfoModal({
  open,
  onOpenChange,
  subcategoryName,
  initialInfo,
  onSave,
}: SubcategoryInfoModalProps) {
  const [info, setInfo] = useState<SubcategoryInfo>(emptyInfo)

  useEffect(() => {
    if (open) {
      setInfo({ ...emptyInfo, ...(initialInfo || {}) })
    }
  }, [open, initialInfo])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSave(info)
    onOpenChange(false)
  }

  const set = (key: keyof SubcategoryInfo) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setInfo((prev) => ({ ...prev, [key]: e.target.value }))

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Info className="h-4 w-4" />
              Informações — {subcategoryName}
            </DialogTitle>
            <DialogDescription>
              Esses dados aparecem no card da subcategoria pra quem abrir o link compartilhado (tipo uma tabela de valores).
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="sub-price">Preço</Label>
              <div className="flex gap-2">
                <select
                  value={info.currency}
                  onChange={(e) => setInfo((prev) => ({ ...prev, currency: e.target.value }))}
                  className="h-10 w-24 shrink-0 rounded-md border border-input bg-transparent px-2 text-sm"
                >
                  {CURRENCY_OPTIONS.map((c) => (
                    <option key={c.code} value={c.code}>{c.symbol}</option>
                  ))}
                </select>
                <Input
                  id="sub-price"
                  value={info.price}
                  onChange={set("price")}
                  placeholder="Ex: 80,00"
                  inputMode="decimal"
                  className="flex-1 min-w-0"
                />
              </div>
              <p className="text-xs text-muted-foreground">Só o número — o símbolo da moeda escolhida é adicionado automaticamente na exibição.</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-2">
                <Label htmlFor="sub-fabric">Tecido</Label>
                <Input
                  id="sub-fabric"
                  value={info.fabric}
                  onChange={set("fabric")}
                  placeholder="Ex: Dry Manchester ou Dry Air"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="sub-sleeves">Mangas</Label>
                <Input
                  id="sub-sleeves"
                  value={info.sleeves}
                  onChange={set("sleeves")}
                  placeholder="Ex: Com ou sem pala anatômica"
                />
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="sub-collar">Gola</Label>
              <Input
                id="sub-collar"
                value={info.collar}
                onChange={set("collar")}
                placeholder="Ex: Redonda, V, Paulista ou Esportiva"
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="sub-differentials">Diferenciais</Label>
              <Textarea
                id="sub-differentials"
                value={info.differentials}
                onChange={set("differentials")}
                placeholder="Ex: Possibilidade de recorte lateral com segundo tecido"
                rows={2}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="sub-benefits">Brindes / benefícios exclusivos</Label>
              <Textarea
                id="sub-benefits"
                value={info.benefits}
                onChange={set("benefits")}
                placeholder={"Um por linha, ex:\n1 mala personalizada para o time\n1 bandeira (1,00 x 1,50 m)\n1 faixa de capitão"}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit">Salvar</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}